import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, SkipForward, ChevronDown, ChevronUp } from './Icons';
import ThermalAnalysis from './ThermalAnalysis';
import './DeviceThermals.css';

// viewBox: 0 0 50 110. Back of phone (teardown view).
const THERMAL_ZONES = [
  { id: 'z1',  label: '1', fullLabel: '1 - Top Left',
    sensors: ['ac', 'sdr0', 'sdr1'],
    x: 4, y: 5, w: 13, h: 12 },
  { id: 'z2',  label: '2', fullLabel: '2 - Top Center',
    sensors: ['cpu-1-4', 'mmw2', 'mmw3'],
    x: 18.5, y: 5, w: 13, h: 12 },
  { id: 'z3',  label: '3', fullLabel: '3 - Top Right',
    sensors: ['cpu-0-3', 'cpu-1-2', 'cpu-1-6', 'cpuss-2', 'mdmss-2', 'video'],
    x: 33, y: 5, w: 13, h: 12 },

  { id: 'z4',  label: '4', fullLabel: '4 - Med Cameras',
    sensors: [],
    x: 4, y: 19, w: 13, h: 12 },
  { id: 'z5',  label: '5', fullLabel: '5 - Main Flash',
    sensors: [],
    x: 18.5, y: 19, w: 13, h: 12 },
  { id: 'z6',  label: '6', fullLabel: '6 - Main Board Mid-Right',
    sensors: ['cpu-0-1', 'cpu-1-0', 'cpu-1-1', 'cpu-1-3', 'cpu-1-5', 'cpu-1-8',
              'cpuss-1', 'cpuss-3', 'ddr', 'gpuss-1', 'mdmss-3', 'mmw_pa2',
              'nspss-0', 'nspss-1', 'pa', 'pm8350_tz', 'pmr735b_tz', 'xo-therm'],
    x: 33, y: 19, w: 13, h: 12 },

  { id: 'z7',  label: '7', fullLabel: '7 - Left Main/Sub',
    sensors: [],
    x: 4, y: 33, w: 13, h: 12 },
  { id: 'z8',  label: '8', fullLabel: '8 - Mid Main/Sub',
    sensors: [],
    x: 18.5, y: 33, w: 13, h: 12 },
  { id: 'z9',  label: '9', fullLabel: '9 - Right Main',
    sensors: ['aoss-0', 'battery', 'battery_temp_c', 'camera-0', 'camera-1',
              'cpu-0-0', 'cpu-0-2', 'cpu-1-7', 'cpuss-0', 'gpuss-0',
              'mdmss-0', 'mdmss-1', 'pm8350c_tz', 'pm8450_tz',
              'sdr-mmw-therm', 'usb-therm'],
    x: 33, y: 33, w: 13, h: 12 },

  { id: 'z10', label: '10', fullLabel: '10 - Batt PCB Left',
    sensors: ['aoss-1'],
    x: 4, y: 47, w: 13, h: 8 },
  { id: 'z11', label: '11', fullLabel: '11 - Batt PCB Center',
    sensors: [],
    x: 18.5, y: 47, w: 13, h: 8 },
  { id: 'z12', label: '12', fullLabel: '12 - Batt PCB Right',
    sensors: [],
    x: 33, y: 47, w: 13, h: 8 },

  { id: 'z14', label: '14', fullLabel: '14 - Sub Board Far Left',
    sensors: [],
    x: 4, y: 92, w: 5.5, h: 12 },
  { id: 'z15', label: '15', fullLabel: '15 - Sub Board Mid Left',
    sensors: [],
    x: 10, y: 92, w: 5.5, h: 12 },
  { id: 'z16', label: '16', fullLabel: '16 - Sub Board Mid',
    sensors: [],
    x: 16, y: 92, w: 5.5, h: 12 },
  { id: 'z17', label: '17', fullLabel: '17 - Sub Board USB',
    sensors: ['nspss-2', 'pmr735a_tz'],
    x: 22, y: 92, w: 5.5, h: 12 },
];

const THERMAL_STATUS_LABELS = {
  '-1': 'Unknown', 0: 'None', 1: 'Light', 2: 'Moderate',
  3: 'Severe', 4: 'Critical', 5: 'Emergency', 6: 'Shutdown',
};
const THERMAL_STATUS_COLORS = {
  '-1': '#888', 0: '#4CAF50', 1: '#8BC34A', 2: '#FFC107',
  3: '#FF9800', 4: '#F44336', 5: '#D32F2F', 6: '#B71C1C',
};

function tempToColor(temp) {
  if (temp === null || temp === undefined || isNaN(temp)) return '#555';
  const t = Math.max(20, Math.min(70, temp));
  const n = (t - 20) / 50;
  if (n < 0.3) return lerpColor('#4FC3F7', '#4CAF50', n / 0.3);
  if (n < 0.5) return lerpColor('#4CAF50', '#FFEB3B', (n - 0.3) / 0.2);
  if (n < 0.7) return lerpColor('#FFEB3B', '#FF9800', (n - 0.5) / 0.2);
  return lerpColor('#FF9800', '#F44336', (n - 0.7) / 0.3);
}

function lerpColor(a, b, t) {
  const ar = parseInt(a.slice(1,3),16), ag = parseInt(a.slice(3,5),16), ab = parseInt(a.slice(5,7),16);
  const br = parseInt(b.slice(1,3),16), bg = parseInt(b.slice(3,5),16), bb = parseInt(b.slice(5,7),16);
  const r = Math.round(ar+(br-ar)*t), g = Math.round(ag+(bg-ag)*t), bl = Math.round(ab+(bb-ab)*t);
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${bl.toString(16).padStart(2,'0')}`;
}

function getZoneTemp(zone, tempData) {
  if (!tempData) return null;
  const vals = zone.sensors.map(s => tempData[s]).filter(v => v != null && v > 0);
  return vals.length ? Math.max(...vals) : null;
}

// ==================== Main Component ====================

const DeviceThermals = ({ healthLogs, deviceId }) => {
  const [page, setPage] = useState('heatmap');
  const [logIndex, setLogIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(5);
  const [hoveredZone, setHoveredZone] = useState(null);
  const [sensorPanelOpen, setSensorPanelOpen] = useState(true);
  const [showPhoto, setShowPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const animRef = useRef(null);
  const indexRef = useRef(0);

  const thermalLogs = useMemo(() => {
    if (!healthLogs) return [];
    return healthLogs.filter(log => log.tempHealth && typeof log.tempHealth === 'object');
  }, [healthLogs]);

  useEffect(() => {
    if (thermalLogs.length > 0 && logIndex === null) setLogIndex(thermalLogs.length - 1);
  }, [thermalLogs, logIndex]);

  const currentLog = thermalLogs[logIndex] || null;
  const tempData = currentLog?.tempHealth || null;
  const deviceHealth = currentLog?.deviceHealth || null;

  const thermalStatus = tempData?.thermal_status ?? -1;
  const thermalStatusName = tempData?.thermal_status_name || THERMAL_STATUS_LABELS[thermalStatus] || 'Unknown';
  const headroom = tempData?.headroom;
  const chargingStatus = tempData?.charging_status || deviceHealth?.chargingStatus || '--';
  const chargingPowerMw = deviceHealth?.chargingPower;
  const chargingPowerW = chargingPowerMw != null ? (chargingPowerMw / 1000).toFixed(1) : null;
  const batteryLevel = deviceHealth?.batteryLevel;

  const zoneTemps = useMemo(() => {
    const m = {};
    THERMAL_ZONES.forEach(z => { m[z.id] = getZoneTemp(z, tempData); });
    return m;
  }, [tempData]);

  // Playback
  useEffect(() => {
    if (!isPlaying || thermalLogs.length === 0) {
      if (animRef.current) { clearInterval(animRef.current); animRef.current = null; }
      return;
    }
    const interval = Math.max(50, 1000 / playSpeed);
    animRef.current = setInterval(() => {
      indexRef.current += 1;
      if (indexRef.current >= thermalLogs.length) {
        indexRef.current = thermalLogs.length - 1;
        setIsPlaying(false);
      }
      setLogIndex(indexRef.current);
    }, interval);
    return () => { if (animRef.current) clearInterval(animRef.current); };
  }, [isPlaying, playSpeed, thermalLogs.length]);

  useEffect(() => { indexRef.current = logIndex || 0; }, [logIndex]);

  const handleSliderChange = useCallback((e) => {
    setLogIndex(parseInt(e.target.value));
    setIsPlaying(false);
  }, []);

  const handlePlayPause = useCallback(() => {
    if (logIndex >= thermalLogs.length - 1) {
      setLogIndex(0);
      indexRef.current = 0;
    }
    setIsPlaying(p => !p);
  }, [logIndex, thermalLogs.length]);

  const jumpToLatest = useCallback(() => {
    setLogIndex(thermalLogs.length - 1);
    setIsPlaying(false);
  }, [thermalLogs.length]);

  const formatTimestamp = (ts) => {
    if (!ts) return '--';
    return new Date(ts).toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  };

  if (!healthLogs || healthLogs.length === 0) {
    return <div className="device-thermals empty-state"><p>No health data available.</p></div>;
  }
  if (thermalLogs.length === 0) {
    return (
      <div className="device-thermals empty-state">
        <p>No thermal sensor data found.</p>
        <p className="hint">Thermal data requires the updated logging firmware.</p>
      </div>
    );
  }

  const hoveredZoneObj = THERMAL_ZONES.find(z => z.id === hoveredZone);

  return (
    <div className="device-thermals">
      {/* Status bar */}
      <div className="thermal-status-bar">
        <div className="status-item">
          <span className="status-label">Status</span>
          <span className="status-badge" style={{ background: THERMAL_STATUS_COLORS[thermalStatus] || '#888' }}>
            {thermalStatusName}
          </span>
        </div>
        <div className="status-item">
          <span className="status-label">Headroom</span>
          <div className="headroom-bar-wrapper">
            <div className="headroom-bar-fill" style={{
              width: `${Math.min(100, (headroom || 0) * 100)}%`,
              background: headroom >= 0.9 ? '#F44336' : headroom >= 0.7 ? '#FF9800' : '#4CAF50',
            }} />
            <span className="headroom-value">{headroom != null ? headroom.toFixed(2) : '--'}</span>
          </div>
        </div>
        <div className="status-item">
          <span className="status-label">Batt</span>
          <span className="status-value">{tempData?.battery_temp_c != null ? `${tempData.battery_temp_c.toFixed(1)}°C` : '--'}</span>
        </div>
        <div className="status-item">
          <span className="status-label">Charge</span>
          <span className="status-value">{chargingStatus}{chargingPowerW != null ? ` ${chargingPowerW}W` : ''}</span>
        </div>
        {batteryLevel != null && (
          <div className="status-item">
            <span className="status-label">Level</span>
            <span className="status-value">{batteryLevel}%</span>
          </div>
        )}
        <div className="status-item">
          <span className="status-label">Log</span>
          <span className="status-value">{logIndex + 1}/{thermalLogs.length}</span>
        </div>

        <div className="thermal-page-tabs">
          <button className={`thermal-page-btn ${page === 'heatmap' ? 'active' : ''}`} onClick={() => setPage('heatmap')}>
            Heatmap
          </button>
          <button className={`thermal-page-btn ${page === 'timeseries' ? 'active' : ''}`} onClick={() => setPage('timeseries')}>
            Analysis
          </button>
        </div>
      </div>

      {/* ========= HEATMAP PAGE ========= */}
      <div className="heatmap-page" style={{ display: page === 'heatmap' ? 'flex' : 'none' }}>
        <div className="thermals-body">
          {showPhoto && (
            <div className="reference-photo-panel">
              {photoError ? (
                <div className="photo-placeholder">
                  <p>Place your teardown image at:</p>
                  <code>public/phone-teardown.png</code>
                  <p>then reload.</p>
                </div>
              ) : (
                <img
                  src={`${process.env.PUBLIC_URL}/phone-teardown.png`}
                  alt="Phone teardown"
                  className="reference-photo-img"
                  onError={() => setPhotoError(true)}
                />
              )}
            </div>
          )}

          <div className="phone-heatmap-container">
            <button className={`photo-toggle-btn ${showPhoto ? 'active' : ''}`}
              onClick={() => { setShowPhoto(p => !p); setPhotoError(false); }}>
              {showPhoto ? 'Hide Photo' : 'Show Photo'}
            </button>

            <svg viewBox="0 0 50 110" className="phone-svg" preserveAspectRatio="xMidYMid meet">
              <rect x="2" y="2" width="46" height="106" rx="5" ry="5" className="phone-outline" />
              <rect x="5" y="57" width="40" height="33" rx="3"
                fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" strokeDasharray="2,1" />
              <text x="25" y="74" textAnchor="middle" className="battery-gap-label">Battery</text>

              {THERMAL_ZONES.map(zone => {
                const temp = zoneTemps[zone.id];
                const color = tempToColor(temp);
                const isHovered = hoveredZone === zone.id;
                return (
                  <g key={zone.id}>
                    <rect x={zone.x} y={zone.y} width={zone.w} height={zone.h} rx="1"
                      fill={color} opacity={isHovered ? 1 : 0.8}
                      stroke={isHovered ? '#fff' : 'rgba(255,255,255,0.2)'}
                      strokeWidth={isHovered ? 0.5 : 0.2}
                      className="thermal-zone-rect"
                      onMouseEnter={() => setHoveredZone(zone.id)}
                      onMouseLeave={() => setHoveredZone(null)} />
                    <text x={zone.x + zone.w/2} y={zone.y + zone.h*0.36}
                      textAnchor="middle" dominantBaseline="central"
                      className="zone-label" pointerEvents="none">{zone.label}</text>
                    <text x={zone.x + zone.w/2} y={zone.y + zone.h*0.72}
                      textAnchor="middle" dominantBaseline="central"
                      className="zone-temp" pointerEvents="none">
                      {temp != null ? `${temp.toFixed(1)}°` : '--'}
                    </text>
                  </g>
                );
              })}
            </svg>

            <div className="color-scale">
              <div className="scale-gradient" />
              <div className="scale-labels">
                <span>20°C</span><span>35°C</span><span>50°C</span><span>65°C+</span>
              </div>
            </div>

            {hoveredZoneObj && tempData && (
              <div className="zone-tooltip">
                <div className="tooltip-title">{hoveredZoneObj.fullLabel}</div>
                {hoveredZoneObj.sensors.length === 0 && (
                  <div className="tooltip-sensor"><span className="tooltip-name" style={{ color: '#666' }}>No peak sensors</span></div>
                )}
                {hoveredZoneObj.sensors.map(s => (
                  <div key={s} className="tooltip-sensor">
                    <span className="tooltip-dot" style={{ background: tempToColor(tempData[s]) }} />
                    <span className="tooltip-name">{s}</span>
                    <span className="tooltip-val">{tempData[s] != null ? `${tempData[s].toFixed(1)}°C` : '--'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="sensor-panel">
            <div className="sensor-panel-header" onClick={() => setSensorPanelOpen(p => !p)}>
              <span>All Sensors</span>
              {sensorPanelOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
            {sensorPanelOpen && (
              <div className="sensor-panel-body">
                {THERMAL_ZONES.filter(z => z.sensors.length > 0).map(zone => (
                  <div key={zone.id} className="sensor-group">
                    <div className="sensor-group-title">{zone.fullLabel}</div>
                    {zone.sensors.map(s => {
                      const val = tempData?.[s];
                      return (
                        <div key={s} className="sensor-row">
                          <span className="sensor-dot" style={{ background: tempToColor(val) }} />
                          <span className="sensor-name">{s}</span>
                          <span className="sensor-val">{val != null ? `${val.toFixed(1)}°C` : '--'}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="thermal-scrubber">
          <div className="scrub-controls">
            <button onClick={handlePlayPause} className="scrub-btn play">{isPlaying ? <Pause size={16}/> : <Play size={16}/>}</button>
            <button onClick={jumpToLatest} className="scrub-btn"><SkipForward size={16}/></button>
            <select value={playSpeed} onChange={e => setPlaySpeed(parseInt(e.target.value))} className="scrub-speed">
              <option value={1}>1x</option><option value={2}>2x</option><option value={5}>5x</option>
              <option value={10}>10x</option><option value={20}>20x</option>
            </select>
          </div>
          <input type="range" min={0} max={thermalLogs.length-1} value={logIndex ?? 0}
            onChange={handleSliderChange} className="scrub-slider" />
          <div className="scrub-timestamp">{formatTimestamp(currentLog?.timestamp)}</div>
        </div>
      </div>

      {/* ========= ANALYSIS PAGE (always mounted, toggled via display) ========= */}
      <ThermalAnalysis
        primaryDeviceId={deviceId}
        primaryThermalLogs={thermalLogs}
        active={page === 'timeseries'}
      />
    </div>
  );
};

export default DeviceThermals;
